import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
    ICustomerDetails,
    ICustomerDirectory,
    ICustomerRelations,
    ICustomFields,
} from '@dxp/core/models';
import { JsonPatchData, LOGGER_REQUEST_NAME } from '@dxp/core/types';
import { environment } from '@dxp/core/environment';

const hasNameFilter =
    '(length(FirstName) gt 0 or length(MiddleName) gt 0 or length(LastName) gt 0)';
const hasPhoneFilter = 'PhoneNumbers/any()';
const hasEmailFilter = 'length(Email) gt 0';

@Injectable({
    providedIn: 'root',
})
export class CustomerApiService {
    readonly #http = inject(HttpClient);

    getCustomer(
        customerId: string,
        expand?: string[],
    ): Observable<ICustomerDetails> {
        const params = expand
            ? new HttpParams().set('$expand', expand.join(','))
            : undefined;

        return this.#http.get<ICustomerDetails>(
            `${environment.apiBaseUrl}/customer-manager/v3/customers(${customerId})`,
            {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Get Customer)',
                ),
            },
        );
    }

    getCustomers(
        count: boolean,
        expand: string[],
        orderBy: string,
        skip: number,
        top: number,
        filters: {
            requiresName?: boolean;
            requiresEmail?: boolean;
            requiresPhone?: boolean;
            businessUnitId: string | null | undefined;
        },
        excludeCustomer?: string,
        searchQuery?: string,
        equalsQuery?: string,
    ): Observable<ICustomerDirectory> {
        const businessUnitFilter = filters.businessUnitId
            ? `BusinessUnitId eq '${filters.businessUnitId}'`
            : 'BusinessUnitId eq null';

        const searchFilter = searchQuery
            ? this.#buildSearchFilter(searchQuery)
            : '';

        const equalsFilter = equalsQuery
            ? this.#buildSearchFilter(equalsQuery, { exact: true })
            : '';

        const excludeCustomerFilter = excludeCustomer
            ? `CustomerId ne ${excludeCustomer}`
            : '';

        const filter = [
            filters.requiresName ? hasNameFilter : '',
            filters.requiresPhone ? hasPhoneFilter : '',
            filters.requiresEmail ? hasEmailFilter : '',
            filters.businessUnitId !== undefined ? businessUnitFilter : '',
            searchQuery ? searchFilter : '',
            equalsQuery ? equalsFilter : '',
            excludeCustomerFilter,
        ]
            .filter(x => !!x)
            .join(' and ');

        let params = new HttpParams()
            .set('$count', count.toString())
            .set('$expand', expand.join(','))
            .set('$skip', skip.toString())
            .set('$top', top.toString())
            .set('$orderby', orderBy);

        if (filter.length > 0) {
            params = params.set('$filter', filter);
        }

        return this.#http
            .get<{
                '@odata.count': number;
                value: ICustomerDetails[];
            }>(`${environment.apiBaseUrl}/customer-manager/v3/customers`, {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Get Customers)',
                ),
            })
            .pipe(
                map(response => {
                    return {
                        count: response['@odata.count'],
                        customerDirectory: response.value,
                    };
                }),
            );
    }

    addCustomer(customer: ICustomerDetails): Observable<ICustomerDetails> {
        return this.#http.post<ICustomerDetails>(
            environment.rbacUrl + '/v3/customers',
            customer,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Add Customer)',
                ),
            },
        );
    }

    updateCustomer(
        customer: ICustomerDetails,
        patchData: JsonPatchData,
    ): Observable<ICustomerDetails> {
        return this.#http.patch<ICustomerDetails>(
            environment.rbacUrl + `/v3/customers/${customer.customerId}`,
            patchData,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Update Customer)',
                ),
            },
        );
    }

    mergeCustomers(
        sourceCustomerId: string,
        targetCustomerId: string,
    ): Observable<void> {
        return this.#http.post<void>(
            environment.rbacUrl + '/v3/customers/merge',
            {
                sourceCustomerId,
                targetCustomerId,
            },
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Merge Customer)',
                ),
            },
        );
    }

    getCustomFields(businessUnitId: string | null): Observable<ICustomFields> {
        const params = new HttpParams().set(
            'businessUnitId',
            businessUnitId ?? '',
        );

        return this.#http.get<ICustomFields>(
            `${environment.apiBaseUrl}/customer-manager/customers/fields`,
            {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Get Custom Fields)',
                ),
            },
        );
    }

    getRelations(customerId: string): Observable<ICustomerRelations> {
        return this.#http.get<ICustomerRelations>(
            `${environment.odataBaseUrl}/extras/customers/${customerId}/relations`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Customer Service (Get Relations)',
                ),
            },
        );
    }

    #buildSearchFilter = (
        searchQuery: string,
        opts: { matchAllWords?: boolean; exact?: boolean } = {
            matchAllWords: true,
            exact: false,
        },
    ): string => {
        const q = (searchQuery ?? '').trim();
        if (!q) return '';

        const escape = (s: string) => s.replace(/'/g, "''");
        const full = escape(q);
        const terms = q.split(/\s+/).map(escape).filter(Boolean);

        const buildClause = (field: string, value: string) =>
            opts.exact
                ? `${field} eq '${value}'`
                : `contains(${field}, '${value}')`;

        const termNameAndFieldsClauses = terms.map(
            t =>
                `(${buildClause('FirstName', t)} or ${buildClause('MiddleName', t)} or ${buildClause('LastName', t)} or CustomFields/any(p: ${buildClause('p/value', full)}))`,
        );
        const joiner = opts.matchAllWords ? ' and ' : ' or ';
        const namesAndFieldsClause = termNameAndFieldsClauses.length
            ? `(${termNameAndFieldsClauses.join(joiner)})`
            : '';

        const emailPhoneClause = `(${buildClause('Email', full)} or PhoneNumbers/any(p: ${buildClause('p/number', full)}))`;

        return namesAndFieldsClause
            ? `(${namesAndFieldsClause} or ${emailPhoneClause})`
            : `(${emailPhoneClause})`;
    };
}
